// src/pages/Home.tsx — one home screen.
//
// The built-in overview and the dashboards people ask for are the same page: a fixed
// header, a rail of views, and a body that swaps. Saved views are tabs rather than a
// gallery, and any of them can be made the view that opens on /app — so a finance
// director lands on arrears and a property manager lands on the overview, without
// either of them navigating.
//
// The Overview body opens with the director summary (merged from the old Director page): needs me, four story cards
// and what could bite — each card drawn only when the viewer's permissions feed it a figure — then the home blocks.
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, ChevronDown, Home as HomeIcon, Loader2, MoreHorizontal, Pin, PinOff, Share2, Sparkles, SquarePen, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../stores/auth';
import { useCan, useCanPage } from '../hooks/usePermission';
import { firstLettingsPath, isLettingsDesk } from '../lib/lettings';
import { Overview } from '../components/home/Overview';
import { DashboardView } from '../components/home/DashboardView';
import { DashboardCards, PeriodToggle } from '../components/home/DashboardCards';
import { directorApi, hasPeriodFigures, type Period } from '../lib/director';
import { Button, Field, Input, Modal } from '../components/ui';
import { Confirm, FormError } from '../components/forms/common';
import type { Dashboard } from '../types/api';
import { PageLoader } from '../components/BrandLoader';

const EXAMPLES = [
  'Occupancy and rent collection across the London portfolio this year',
  'Everything I need on arrears — aged buckets, worst properties, trend',
  'Maintenance health: SLA performance, open jobs by age, spend by property',
  'Compliance risk — certificates expiring and deposits not yet protected',
];

/** How many view tabs sit on the rail before the rest fold into "More". */
const VISIBLE_TABS = 4;
/** The saved-views rail (Overview · saved views · New view) is hidden for now (19 Oct 2026); /app/v/:id still opens a view. */
const SHOW_VIEWS = false;

export default function HomePage({ overview = false }: { overview?: boolean }) {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuth((s) => s.user);
  const restore = useAuth((s) => s.restore);

  // Hooks are called unconditionally, then combined — a permission check must never
  // decide whether a hook runs.
  const canDashboards = useCanPage('dashboards');
  const canCreate = useCan('dashboards.create');
  const canUpdate = useCan('dashboards.update');
  const canDelete = useCan('dashboards.delete');
  const canShare = useCan('dashboards.share');
  const canReadWorkOrders = useCan('workOrders.read');
  const canReadResidents = useCan('residents.read');
  const isResident = !!user && !user.allAccess && user.scope.type === 'OWN' && !canReadWorkOrders && canReadResidents;
  // An external agent (Task G) has no dashboard: their home is the reservation sheet.
  const canDashboard = useCan('dashboard.read');
  const canReservations = useCanPage('reservations');
  // A Lettings-only member of staff (reservations.update, no dashboard — the Letting Accelerator since 3 Nov 2026) is not
  // an outside agent: they land on the first Lettings page they can open (lib/lettings.ts).
  const canAccessPage = useAuth((s) => s.canAccessPage);
  const can = useAuth((s) => s.can);
  const lettingsHome = isLettingsDesk(user, can) ? firstLettingsPath(canAccessPage) : null;
  const isAgent = !!user && !user.allAccess && !canDashboard && canReservations && !lettingsHome;
  // An investor (Task K) lands on their companies and statements: read-only, scoped to their companies, no work queue.
  const canOwnerStatements = useCanPage('ownerStatements');
  const canBuildStatements = useCan('ownerStatements.create');
  const canMyDay = useCan('myDay.read');
  // A contractor (Task U) lands on their own papers; their jobs are one tap away.
  const canContractor = useCanPage('contractorPortal');
  const isContractor = !!user && !user.allAccess && user.scope.type === 'OWN' && canContractor && !canMyDay;
  // An applicant (signed up in the app, not a resident yet) lands on their booking — or the way to the homes.
  const canApplicantPortal = useCanPage('applicantPortal');
  const isApplicant = !!user && !user.allAccess && user.scope.type === 'OWN' && canApplicantPortal && !canReadResidents;
  const isInvestor = !!user && !user.allAccess && user.scope.type === 'ENTITY' && canOwnerStatements && !canBuildStatements && !canMyDay;

  const [composing, setComposing] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<Dashboard | null>(null);
  const [renameTo, setRenameTo] = useState('');
  const [removing, setRemoving] = useState<Dashboard | null>(null);
  const [showMore, setShowMore] = useState(false);
  const railRef = useRef<HTMLDivElement>(null);
  const [params] = useSearchParams();
  const [period, setPeriod] = useState<Period>('month');

  const { data: dashboards } = useQuery({
    queryKey: ['dashboards'],
    queryFn: async () => (await api.get<Dashboard[]>('/ai/dashboards')).data,
    enabled: canDashboards && !isResident,
  });

  const homeId = (user?.metadata?.preferences as { homeDashboardId?: string } | undefined)?.homeDashboardId ?? null;
  const list = dashboards ?? [];
  // /app honours the pinned view; /app/overview forces the built-in one.
  const activeId = id ?? (overview ? null : (homeId && list.some((d) => d.id === homeId) ? homeId : null));
  const active = list.find((d) => d.id === activeId) ?? null;
  const redirected = !!lettingsHome || isResident || isAgent || isInvestor || isContractor || isApplicant;

  // The director summary feeds the Overview only; every figure in it is already null where the viewer may not look.
  const summary = useQuery({
    queryKey: ['director', 'summary', period],
    queryFn: () => directorApi.summary(period),
    enabled: canDashboard && !redirected && !activeId,
    placeholderData: (prev) => prev,   // switching period keeps the cards (and the toggle) on screen
  });

  const generate = useMutation({
    mutationFn: async (text: string) => (await api.post<Dashboard>('/ai/dashboards/generate', { prompt: text })).data,
    onSuccess: (d) => { void qc.invalidateQueries({ queryKey: ['dashboards'] }); setComposing(false); setPrompt(''); navigate(`/app/v/${d.id}`); },
  });
  const rename = useMutation({
    mutationFn: async () => api.patch(`/ai/dashboards/${renaming!.id}`, { name: renameTo.trim() }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dashboards'] }); setRenaming(null); },
  });
  const share = useMutation({
    mutationFn: async (d: Dashboard) => api.patch(`/ai/dashboards/${d.id}`, { isShared: !d.isShared }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboards'] }),
  });
  const remove = useMutation({
    mutationFn: async (d: Dashboard) => api.delete(`/ai/dashboards/${d.id}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['dashboards'] }); setRemoving(null); navigate('/app/overview'); },
  });
  const pin = useMutation({
    mutationFn: async (dashboardId: string | null) => api.patch('/auth/preferences', { homeDashboardId: dashboardId }),
    onSuccess: () => { void restore(); setMenuFor(null); },
  });

  // Close the per-view menu on any outside click.
  useEffect(() => {
    if (!menuFor) return;
    const close = (e: MouseEvent) => { if (!railRef.current?.contains(e.target as Node)) setMenuFor(null); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuFor]);

  if (isResident) return <Navigate to="/app/my-home" replace />;
  if (lettingsHome) return <Navigate to={lettingsHome} replace />;
  if (isAgent) return <Navigate to="/app/agent" replace />;
  if (isInvestor) return <Navigate to="/app/entities" replace />;
  if (isContractor) return <Navigate to="/app/my-documents" replace />;
  if (isApplicant) return <Navigate to="/app/my-booking" replace />;

  const firstName = user?.firstName ?? 'there';
  const hour = new Date().getHours();
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  const visible = list.slice(0, VISIBLE_TABS);
  const overflow = list.slice(VISIBLE_TABS);
  const overflowActive = overflow.some((d) => d.id === activeId);

  const tabClass = (on: boolean) =>
    `relative -mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[12.5px] transition ${on ? 'border-bronze-500 font-semibold text-bronze-600' : 'border-transparent font-medium text-ink-500 hover:text-ink-900'}`;

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="eyebrow">{today}</p>
          <h1 className="mt-1 font-display text-[22px] font-semibold tracking-[-0.02em] text-ink-900">{hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening'}, {firstName}</h1>
        </div>
        {!activeId && hasPeriodFigures(summary.data) && <PeriodToggle period={period} onChange={setPeriod} />}
      </div>

      {SHOW_VIEWS && canDashboards && (
        <div ref={railRef} className="flex flex-wrap items-center gap-1 border-b border-ink-200">
          <Link to="/app/overview" className={tabClass(!activeId)}>Overview</Link>

          {visible.map((d) => (
            <span key={d.id} className="relative inline-flex items-center">
              <Link to={`/app/v/${d.id}`} className={tabClass(activeId === d.id)}>
                {homeId === d.id && <Pin className="mr-1 inline h-3 w-3 -translate-y-px" />}
                {d.name}
              </Link>
              {activeId === d.id && (canUpdate || canDelete) && (
                <button onClick={() => setMenuFor(menuFor === d.id ? null : d.id)} className="ml-0.5 rounded-lg p-1 text-ink-400 transition hover:bg-ink-100 hover:text-ink-900" title="View options"><MoreHorizontal className="h-3.5 w-3.5" /></button>
              )}
              {menuFor === d.id && (
                <div className="absolute left-0 top-10 z-30 w-56 overflow-hidden rounded-lg border border-ink-200 bg-white py-1.5 shadow-pop">
                  {canUpdate && <button onClick={() => { setRenaming(d); setRenameTo(d.name); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-ink-700 transition hover:bg-ink-50"><SquarePen className="h-3.5 w-3.5 text-ink-400" /> Rename</button>}
                  <button onClick={() => pin.mutate(homeId === d.id ? null : d.id)} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-ink-700 transition hover:bg-ink-50">
                    {homeId === d.id ? <><PinOff className="h-3.5 w-3.5 text-ink-400" /> Stop opening this first</> : <><HomeIcon className="h-3.5 w-3.5 text-ink-400" /> Make this my home view</>}
                  </button>
                  {canShare && <button onClick={() => { share.mutate(d); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-ink-700 transition hover:bg-ink-50"><Share2 className="h-3.5 w-3.5 text-ink-400" /> {d.isShared ? 'Stop sharing' : 'Share with my team'}</button>}
                  {canDelete && <button onClick={() => { setRemoving(d); setMenuFor(null); }} className="flex w-full items-center gap-2 px-3.5 py-2 text-left text-[12.5px] text-red-600 transition hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /> Delete</button>}
                </div>
              )}
            </span>
          ))}

          {overflow.length > 0 && (
            <span className="relative">
              <button onClick={() => setShowMore((v) => !v)} className={tabClass(overflowActive)}>More <ChevronDown className="ml-0.5 inline h-3 w-3" /></button>
              {showMore && (
                <div className="absolute left-0 top-10 z-30 max-h-72 w-64 overflow-y-auto rounded-lg border border-ink-200 bg-white py-1.5 shadow-pop">
                  {overflow.map((d) => (
                    <Link key={d.id} to={`/app/v/${d.id}`} onClick={() => setShowMore(false)} className="block truncate px-3.5 py-2 text-[12.5px] text-ink-700 transition hover:bg-ink-50">{d.name}</Link>
                  ))}
                </div>
              )}
            </span>
          )}

          {canCreate && (
            <Button variant={composing ? 'secondary' : 'ghost'} size="sm" icon="Plus" onClick={() => setComposing((v) => !v)} className="mb-1 ml-auto">New view</Button>
          )}
        </div>
      )}

      {composing && canCreate && (
        <div className="surface relative p-4">
          <div className="relative">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-ink-900"><Sparkles className="h-3.5 w-3.5 text-bronze-500" /> New view</div>
            <div className="mt-3 flex items-end gap-2 rounded-lg border border-ink-200 bg-white p-1.5 transition focus-within:border-bronze-500 focus-within:ring-2 focus-within:ring-bronze-500/30">
              <textarea
                rows={2}
                autoFocus
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && prompt.trim()) generate.mutate(prompt.trim()); }}
                placeholder="e.g. Show me occupancy, arrears and maintenance spend for the London portfolio"
                className="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-[13px] leading-relaxed text-ink-900 outline-none placeholder:text-ink-400"
              />
              <button onClick={() => prompt.trim() && generate.mutate(prompt.trim())} disabled={generate.isPending || !prompt.trim()} className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-bronze-500 text-on-primary transition hover:bg-bronze-600 disabled:opacity-40" title="Create (⌘⏎)">
                {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {EXAMPLES.map((e) => (
                <button key={e} onClick={() => setPrompt(e)} className="h-7 rounded-lg border border-ink-200 bg-white px-2.5 text-[12px] text-ink-600 transition hover:border-ink-300 hover:text-ink-900">{e}</button>
              ))}
            </div>
            {generate.isPending && <p className="mt-3 text-[12.5px] text-ink-500">Choosing reports, chart types and layout…</p>}
            {generate.isError && <p className="mt-3 text-[12.5px] text-red-600">{(generate.error as Error)?.message ?? 'Could not build that view.'}</p>}
          </div>
        </div>
      )}

      {activeId && !active ? (
        <PageLoader />
      ) : active ? (
        <DashboardView dashboard={active} />
      ) : (
        <>
          {summary.isLoading && <PageLoader />}
          {summary.error && <p className="text-[13px] text-red-600">The summary could not load: {(summary.error as Error).message}</p>}
          {summary.data && <DashboardCards summary={summary.data} period={period} tab={params.get('tab')} highlightId={params.get('ai')} />}
          <Overview />
          {SHOW_VIEWS && canCreate && !list.length && !composing && (
            <p className="text-[12.5px] text-ink-500">
              Want a view of your own? <button onClick={() => setComposing(true)} className="font-medium text-bronze-600 hover:underline">Ask for one →</button>
            </p>
          )}
        </>
      )}

      <Modal open={!!renaming} onClose={() => setRenaming(null)} title="Rename this view"
        footer={<><Button variant="secondary" onClick={() => setRenaming(null)}>Cancel</Button><Button onClick={() => rename.mutate()} disabled={!renameTo.trim() || rename.isPending}>{rename.isPending ? 'Saving…' : 'Save'}</Button></>}>
        <div className="space-y-2.5">
          <Field label="Name"><Input value={renameTo} onChange={(e) => setRenameTo(e.target.value)} className="w-full" autoFocus /></Field>
          <FormError error={rename.error} />
        </div>
      </Modal>

      <Confirm open={!!removing} onClose={() => setRemoving(null)} title={`Delete “${removing?.name ?? ''}”?`}
        body="The view goes for everyone it was shared with. The reports behind it are untouched — you can always ask for it again."
        confirmLabel="Delete view" pending={remove.isPending} error={remove.error} onConfirm={() => remove.mutate(removing!)} />
    </div>
  );
}
